import { ApiWrapperError } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { API_FETCH_ABORT_SYMBOL } from '@dealstack/frontend/shared/other/react/ApiWrapper/symbols';

/**
 * Whether a failed API call is worth a Sentry event, for the background pollers that
 * catch their own failures rather than letting them surface.
 *
 * Three failure modes are routine rather than defects: a 401 means the session has
 * already ended, an abort means something cancelled the request deliberately, and a
 * failure that `reportTransportError` already sent under its own fingerprint is a
 * duplicate with a worse title. That last one is asked of the error directly rather
 * than re-derived: the reporter only wraps the fetch, so a TypeError raised elsewhere
 * in the API layer looks the same but was never reported.
 *
 * Everything else reports, including a response-less failure raised inside the API
 * layer, which is a real defect and must not be swallowed with the offline noise.
 */
export const isReportableApiFailure = (error: unknown): boolean => {
	if (!(error instanceof ApiWrapperError)) {
		return true;
	}
	if (error.status === 401) {
		return false;
	}
	if (error.status !== undefined) {
		return true;
	}
	if (error.symbol === API_FETCH_ABORT_SYMBOL) {
		return false;
	}
	return !error.alreadyReported;
};
